import { useState } from 'react';

interface ImageSlotProps {
  label: string;
  imageUrl: string | null;
  isOverride: boolean;
  /** No upload, no reset — the slot only shows what is attached. */
  readOnly?: boolean;
  /**
   * 'overlay' (default): caption bar over the image. 'tile': square image with
   * the label, status and actions in a row underneath, always visible.
   */
  variant?: 'overlay' | 'tile';
  onUpload: (file: File) => Promise<void>;
  onClear: () => Promise<void>;
}

export function ImageSlot({
  label,
  imageUrl,
  isOverride,
  readOnly = false,
  variant = 'overlay',
  onUpload,
  onClear,
}: ImageSlotProps) {
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setLoading(true);
    try {
      await onUpload(file);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) void handleFile(e.target.files[0]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) void handleFile(e.dataTransfer.files[0]);
  };

  const handleClear = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      await onClear();
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'tile') {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <label
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative flex aspect-[16/10] items-center justify-center overflow-hidden rounded-lg border bg-[var(--co-bg)] transition-colors ${
            imageUrl ? 'border-solid' : 'border-dashed'
          } ${readOnly ? '' : 'cursor-pointer hover:border-[var(--co-brand)]'}`}
          style={{
            borderColor: dragActive ? 'var(--co-brand)' : 'var(--co-border)',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={label} className="h-full w-full object-contain p-3" />
          ) : (
            <span className="flex flex-col items-center gap-1 text-[11px] text-[var(--co-muted)]">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {readOnly ? 'Empty' : 'Upload'}
            </span>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleChange}
            disabled={loading || readOnly}
            className="sr-only"
          />
        </label>

        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[12px] font-medium text-[var(--co-text)]">{label}</span>
          {isOverride ? (
            <span className="flex shrink-0 items-center gap-1.5">
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                custom
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading}
                  aria-label={`Reset ${label}`}
                  className="text-[11px] text-[var(--co-muted)] underline-offset-2 hover:text-[var(--co-text)] hover:underline"
                >
                  Reset
                </button>
              )}
            </span>
          ) : (
            <span className="shrink-0 text-[11px] text-[var(--co-muted)]">
              {imageUrl ? 'default' : 'empty'}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <label
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`group relative flex aspect-[4/3] flex-col overflow-hidden rounded-lg border border-dashed bg-[var(--co-bg)] transition-colors ${
        readOnly ? '' : 'cursor-pointer hover:border-[var(--co-brand)]'
      }`}
      style={{
        borderColor: dragActive ? 'var(--co-brand)' : 'var(--co-border)',
        opacity: loading ? 0.6 : 1,
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={label}
          className="absolute inset-0 h-full w-full object-contain"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[11px] text-[var(--co-muted)]">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>{readOnly ? 'Empty' : 'Upload'}</span>
        </div>
      )}

      {/* Caption bar at the bottom with label + override dot */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
        <span className="truncate text-[11px] font-medium text-white drop-shadow">{label}</span>
        {isOverride && (
          <span
            title="Override active"
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400 ring-2 ring-emerald-400/30"
          />
        )}
      </div>

      {/* Reset (X) — only when override exists; shows on hover */}
      {isOverride && !readOnly && (
        <button
          type="button"
          onClick={handleClear}
          disabled={loading}
          title="Reset to default"
          aria-label={`Reset ${label}`}
          className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
        >
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
          </svg>
        </button>
      )}

      <input
        type="file"
        accept="image/*"
        onChange={handleChange}
        disabled={loading || readOnly}
        className="sr-only"
      />
    </label>
  );
}
